<template>
  <div class="slider" :style="{ width: 'auto', height }">
    <div class="slider-items">
      <slot />
    </div>
    <button
      v-if="controlBtn && slideCount > 1"
      type="button"
      class="slider-btn slider-btn-left"
      aria-label="Previous slide"
      @click="navigate(activeIndex - 1)"
    >
      <i class="slider-icon slider-icon-left" aria-hidden="true" />
    </button>
    <button
      v-if="controlBtn && slideCount > 1"
      type="button"
      class="slider-btn slider-btn-right"
      aria-label="Next slide"
      @click="navigate(activeIndex + 1)"
    >
      <i class="slider-icon slider-icon-right" aria-hidden="true" />
    </button>
    <div v-if="indicators && slideCount > 1" class="slider-indicators slider-indicators-center">
      <span
        v-for="index in slideCount"
        :key="index"
        class="slider-indicator-icon"
        :class="{ 'slider-indicator-active': activeIndex === index - 1 }"
        @click="navigate(index - 1)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, provide, ref, watch } from 'vue';

const props = defineProps({
  interval: { type: Number, default: 4500 },
  speed: { type: Number, default: 1000 },
  autoplay: { type: [Boolean, String], default: true },
  controlBtn: { type: [Boolean, String], default: true },
  indicators: { type: [Boolean, String], default: false },
  animation: { type: String, default: 'fade' },
  height: { type: String, default: '100%' },
  touch: { type: [Boolean, String], default: true },
});

const activeIndex = ref(0);
const slideCount = ref(0);

let autoplayTimer: ReturnType<typeof setInterval> | null = null;

const autoplayEnabled = computed(() => {
  const value = props.autoplay;
  return value === true || value === 'true';
});

const controlBtn = computed(() => {
  const value = props.controlBtn;
  return value === true || value === 'true';
});

const indicators = computed(() => {
  const value = props.indicators;
  return value === true || value === 'true';
});

provide('blockSliderActiveIndex', activeIndex);
provide(
  'blockSliderSpeed',
  computed(() => props.speed)
);

provide('blockSliderRegister', (): number => {
  const index = slideCount.value;
  slideCount.value += 1;

  return index;
});

const goTo = (index: number): void => {
  if (slideCount.value === 0) {
    return;
  }
  activeIndex.value = (index + slideCount.value) % slideCount.value;
};

const next = (): void => goTo(activeIndex.value + 1);

const startAutoplay = (): void => {
  stopAutoplay();
  if (!autoplayEnabled.value || slideCount.value <= 1) {
    return;
  }
  autoplayTimer = setInterval(() => next(), props.interval);
};

const stopAutoplay = (): void => {
  if (autoplayTimer) {
    clearInterval(autoplayTimer);
    autoplayTimer = null;
  }
};

const navigate = (index: number): void => {
  goTo(index);
  startAutoplay();
};

onMounted(() => {
  void nextTick(startAutoplay);
});

watch(slideCount, () => {
  void nextTick(startAutoplay);
});

onBeforeUnmount(stopAutoplay);
</script>
